import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Package, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getOrder } from '../lib/api';
import { useCart } from '../context/CartContext';

export default function CheckoutSuccess() {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id');
  const [status, setStatus] = useState('loading');
  const [order, setOrder] = useState(null);
  const { clearCart } = useCart();

  useEffect(() => {
    const fetchOrder = async () => {
      if (!orderId) {
        setStatus('error');
        return;
      }

      try {
        const res = await getOrder(orderId);
        if (res.data && res.data.payment_status === 'paid') {
          setOrder(res.data);
          setStatus('success');
          clearCart();
        } else {
          setStatus('error');
        }
      } catch (error) {
        console.error('Error fetching order:', error);
        setStatus('error');
      }
    };

    fetchOrder();
  }, [orderId, clearCart]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center" data-testid="checkout-success-page">
        <div className="text-center">
          <Loader2 size={48} className="animate-spin text-[#003366] mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-slate-900">Loading your order...</h1>
          <p className="text-slate-500">Please wait a moment.</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center" data-testid="checkout-success-page">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
            <span className="text-4xl">&#10060;</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Something went wrong</h1>
          <p className="text-slate-500 mb-6">
            We couldn't retrieve your order details. Please contact support if you believe your payment was processed.
          </p>
          <Link to="/cart">
            <Button className="bg-[#003366] hover:bg-[#002855]">
              Return to Cart
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-12" data-testid="checkout-success-page">
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-white rounded-lg border p-8 text-center">
          <div className="w-20 h-20 rounded-full bg-[#10B981]/10 flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={48} className="text-[#10B981]" />
          </div>

          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" data-testid="success-heading">
            Thank you for your order!
          </h1>
          <p className="text-slate-500 mb-6">
            Your payment was successful. We've sent a confirmation to your email.
          </p>

          {order && (
            <div className="bg-slate-50 rounded-lg p-4 mb-6 text-left" data-testid="order-details">
              <div className="flex items-center gap-2 mb-3">
                <Package className="text-[#003366]" />
                <span className="font-semibold">Order #{order.id.slice(0, 8)}</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <span className="text-slate-500">Total</span>
                <span className="font-medium text-right">${order.total.toFixed(2)}</span>
                <span className="text-slate-500">Status</span>
                <span className="font-medium text-right capitalize text-[#10B981]">{order.status}</span>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/products">
              <Button className="bg-[#FFD700] hover:bg-[#F4C430] text-slate-900 font-semibold">
                Continue Shopping <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
            {order && (
              <Link to={`/track-order?order_id=${order.id}`}>
                <Button variant="outline" className="border-[#003366] text-[#003366]">
                  Track Your Order
                </Button>
              </Link>
            )}
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg border p-4 text-center">
            <Package className="mx-auto text-[#003366] mb-2" size={24} />
            <h3 className="font-semibold text-slate-900">Processing</h3>
            <p className="text-sm text-slate-500">Your order is being prepared</p>
          </div>
          <div className="bg-white rounded-lg border p-4 text-center">
            <Package className="mx-auto text-[#C5A059] mb-2" size={24} />
            <h3 className="font-semibold text-slate-900">Shipping</h3>
            <p className="text-sm text-slate-500">2-5 business days</p>
          </div>
          <div className="bg-white rounded-lg border p-4 text-center">
            <Package className="mx-auto text-[#10B981] mb-2" size={24} />
            <h3 className="font-semibold text-slate-900">Updates</h3>
            <p className="text-sm text-slate-500">We'll keep you posted</p>
          </div>
        </div>
      </div>
    </div>
  );
}
